<template>
    <section class="valuation-layout  container" id="mechanical-condition">
        <h1 class="valuation-layout__header">Free Instant Vehicle Valuation</h1>
        <StepBar />
        <StepLink :step="step - 1" />

        <div v-if="mechanicalCondition" class="valuation-layout__container">
            <valuation-amount v-if="valuation" :show-cta="false"
                valuation-title="Your Estimated Vehicle Valuation"
                valuation-text="This estimate will update on the outcome of each step">
            </valuation-amount>

            <vehicle-image />

            <div class="valuation-layout__main-section">
                <h3 class="valuation-layout__title">About your vehicle</h3>
                <p>We’d like to understand a little more about your vehicle’s mechanical condition to reflect its true value. Please answer the questions below to help us provide an even more accurate valuation estimate.</p>

                <div class="rd-card rd-card--border-top valuation-layout__card">
                    <div class="valuation-layout__condition-container">
                        <form @submit.prevent="submit" ref="form" data-form-name="Valuation - Mechanical condition">
                            <div class="service-history__description" v-if="Object.keys(formattedDisplayExtraQuestions).length">
                                <h3 class="card__heading">Flagged mechanical condition issues:</h3>
                                <p>
                                    During a previous Vehicle Health Check at Stellantis &You or your most recent MOT, it was noted that some parts were worn or damaged. Please can you confirm if these have been repaired or replaced since?
                                </p>
                                <div v-for="(value, key) in formattedDisplayExtraQuestions" :key="key">
                                    <QuestionOption
                                        :healthCheckOptions=true
                                        :labelText="getQuestion(key)"
                                        :optionName="key"
                                        validate-message="Please select an option."
                                    />
                                </div>
                            </div>
                            <QuestionOption
                                labelText="Do you have the VSC logbook for this vehicle?"
                                optionName="V5CPresent"
                                validate-message="Please select an option."
                            />
                            <QuestionOption
                                labelText="Has your vehicle ever been used as a taxi or emergency vehicle?"
                                optionName="ServiceVehicle"
                                validate-message="Please select an option."
                            />
                            <QuestionOption
                                labelText="Does your vehicle have any major damage or mechanical faults?"
                                optionName="DamageOrFaults"
                                validate-message="Please select an option."
                            />
                            <QuestionOption
                                labelText="Would you like to keep your private plate?"
                                optionName="KeepRegistration"
                                validate-message="Please select an option."
                            />
                            <QuestionOption
                                labelText="Are all door handles functioning properly?"
                                optionName="FunctioningDoorHandles"
                                validate-message="Please select an option."
                            />
                            <QuestionOption
                                labelText="Is the central locking system functioning properly?"
                                optionName="FunctioningCentralLocking"
                                validate-message="Please select an option."
                            />
                            <QuestionOption
                                labelText="Are there any warning lights displayed on your dashboard?"
                                optionName="WarningLightsDisplaying"
                                validate-message="Please select an option."
                            />
                            <QuestionOption
                                labelText="Are there any oil leaks or unusual engine noises?"
                                optionName="OilLeakOrUnusualEngineNoise"
                                validate-message="Please select an option."
                            />
                            <QuestionOption
                                v-if="displayFrontBrakePadsQuestion"
                                labelText="Are the front brake pads heavily worn"
                                modalTitle="Front brake pads"
                                modalText="If the brake pad thickness is below 3mm, they are considered heavily worn."
                                optionName="FrontBrakesPadsWorn"
                                validate-message="Please select an option."
                                @set-modal="setModal"
                            />
                            <QuestionOption
                                v-if="displayRearBrakePadsQuestion"
                                labelText="Are the rear brake pads heavily worn"
                                modalTitle="Rear brake pads"
                                modalText="If the brake pad thickness is below 3mm, they are considered heavily worn."
                                optionName="RearBrakesPadsWorn"
                                validate-message="Please select an option."
                                @set-modal="setModal"
                            />
                            <QuestionOption
                                labelText="Has your vehicle got a spare or inflation kit present"
                                optionName="SpareOrInflationKitPresent"
                                validate-message="Please select an option."
                            />
                            <QuestionOption
                                labelText="Are all the standard items in the boot present?"
                                modalTitle="Boot items include:"
                                modalText="Parcel Shelf,  Load Cover,  Multi-level boot floor."
                                optionName="ItemsInBootPresent"
                                validate-message="Please select an option."
                                @set-modal="setModal"
                            />

                            <div class="mechanical-condition__key-count-section">
                                <div class="mechanical-condition__header">
                                    <h3 class="card__heading">Number of tyres that are worn/damaged?</h3><div class="question-option__label-info" @click="openDamagedTyresModal">i</div>
                                </div>
                                <RadioOptionButtons
                                    :options="numberWornTyresOptions"
                                    v-model="NumberWornTyres"
                                    name="NumberWornTyres"
                                    validate="radio"
                                    validate-message="Please select an option."
                                    error-target="number-worn-tyres"
                                />
                            </div>

                            <div class="mechanical-condition__key-count-section">
                                <div class="mechanical-condition__header">
                                    <h3 class="card__heading">Is the media screen functioning properly?</h3>
                                </div>
                                <RadioOptionButtons
                                    :options="functioningMediaScreenOptions"
                                    v-model="FunctioningMediaScreen"
                                    name="FunctioningMediaScreen"
                                    validate="radio"
                                    validate-message="Please select an option."
                                    error-target="functioning-media-screen"
                                />
                            </div>

                            <div v-if="selectedVehicle && !selectedVehicle.hide_timing_kit" class="mechanical-condition__key-count-section">
                                <div class="mechanical-condition__header">
                                    <h3 class="card__heading">Has the timing kit on the vehicle been replaced?</h3>
                                </div>
                                <RadioOptionButtons
                                    :options="timingKitReplacedOptions"
                                    v-model="TimingKitReplaced"
                                    name="TimingKitReplaced"
                                    validate="radio"
                                    validate-message="Please select an option."
                                    error-target="timing-kit-replaced"
                                />
                            </div>

                            <div v-if="selectedVehicle && selectedVehicle.battery_range_miles !== null && selectedVehicle.battery_range_miles !== undefined" class="mechanical-condition__key-count-section">
                                <div class="mechanical-condition__header">
                                    <h3 class="card__heading">Is the charging cable present?</h3>
                                </div>
                                <RadioOptionButtons
                                    :options="chargingCablePresentOptions"
                                    v-model="ChargingCablePresent"
                                    name="ChargingCablePresent"
                                    validate="radio"
                                    validate-message="Please select an option."
                                    error-target="charging-cable-present"
                                />
                            </div>

                            <div class="mechanical-condition__key-count-section">
                                <div class="mechanical-condition__header">
                                    <h3 class="card__heading">How many working keys do you have for your vehicle?</h3>
                                </div>
                                <RadioOptionButtons
                                    :options="keyCountOptions"
                                    v-model="NumberOfWorkingKeys"
                                    name="numberOfWorkingKeys"
                                    validate="radio"
                                    validate-message="Please select an option."
                                    error-target="working-key-error"
                                />
                            </div>

                            <div class="mechanical-condition__key-count-section">
                                <div class="mechanical-condition__header">
                                    <h3 class="card__heading">Is your Locking Wheel Nut present?</h3>
                                </div>
                                <RadioOptionButtons
                                    :options="lockingWheelNutPresentOptions"
                                    v-model="LockingWheelNutPresent"
                                    name="lockingWheelNutPresent"
                                    validate="radio"
                                    validate-message="Please select an option."
                                    error-target="locking-wheel-nut-present"
                                />
                            </div>
                        </form>

                        <div class="valuation-layout__button-area">
                            <loading-spinner v-if="submitting" />
                            <template v-else>
                                <button type="button" class="btn btn--primary card__button valuation-layout__button" @click.prevent="submit">
                                    <span>Continue</span>
                                </button>
                                <a v-if="authenticated" href="/my-account/valuations" target="_blank"   class="link   link--primary link--has-icon valuation-layout__button">
                                    Save Progress To My Account
                                </a>
                                <a v-else href="#" class="link  link--primary  link--has-icon valuation-layout__button" @click.prevent="registerAndSave">
                                    Save Progress To My Account
                                </a>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <div class="valuation-layout__vehicle-details">
                <VehicleDetails :displayImage="false"/>
                <button @click="newValuation()" class="btn  btn--full-width btn--primary-inverted ">Start new valuation</button>
            </div>
            <QuestionModal v-if="modalOpen" :is-card="true" :has-close-button="true" @close="closeModal" :modalData="modalData"/>

        </div>
    </section>
</template>

<script>
import QuestionOption from '../Sections/QuestionOption.vue';
import QuestionModal from '../Sections/QuestionModal.vue';

import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { StepBar, StepLink, VehicleDetails, ValuationAmount, RadioOptionButtons, LoadingSpinner, VehicleImage } from "../";

export default {
    components: {
        VehicleDetails,
        StepBar,
        StepLink,
        QuestionOption,
        ValuationAmount,
        RadioOptionButtons,
        LoadingSpinner,
        VehicleImage,
        QuestionModal
    },
    data() {
        return {
            submitting: false,
            validator: false,
            modalData: null,
            modalOpen:false,
            keyCountOptions:[
                { label: '1 key', value: 1 },
                { label: '2 keys', value: 2 },
                { label: '3 keys', value: 3 },
                { label: '4+ keys', value: 4 },
            ],
            numberWornTyresOptions:[
                { label: '0 Tyres', value: 0 },
                { label: '1 Tyre', value: 1 },
                { label: '2 Tyres', value: 2 },
                { label: '3 Tyres', value: 3 },
                { label: '4 Tyres', value: 4 },
            ],
            parcelShelfPresentOptions:[
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
                { label: 'Not Applicable', value: 'Not Applicable' },
            ],
            lockingWheelNutPresentOptions:[
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
                { label: 'Not Applicable', value: 'Not Applicable' },
            ],
            timingKitReplacedOptions:[
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
                { label: 'Not Due', value: 'Not Due' },
                { label: 'Not Applicable', value: 'Not Applicable' },
            ],
            functioningMediaScreenOptions:[
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
                { label: 'Not Applicable', value: 'Not Applicable' },
            ],
            chargingCablePresentOptions:[
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
                { label: 'Not Applicable', value: 'Not Applicable' },
            ],
        };
    },
    methods: {
        ...mapMutations("ValuationExtended", ["setWorkingKeyCount", "setLockingWheelNutPresent", "setTimingKitReplaced", "setFunctioningMediaScreen", "setChargingCablePresent", "setNumberWornTyres"]),
        ...mapActions("ValuationExtended", ["saveAndContinue", "registerAndSave"]),
        ...mapActions("ValuationTool", ["goToPosition", "newValuation"]),

        openDamagedTyresModal() {
            this.setModal({
                title: "Worn/Damaged Tyres",
                text: "If the tread depth is below 3mm, the tyre is considered worn/damaged.",
            });
        },

        submit() {
            if (this.submitting) {
                console.warn("Form is already submitting");
                return;
            }
            this.submitting = true;

            if (this.selectedVehicle && this.selectedVehicle.hide_timing_kit) {
                this.TimingKitReplaced = "Not Applicable"
            }

            if (this.selectedVehicle && this.selectedVehicle?.battery_range_miles == null) {
                this.ChargingCablePresent = "Not Applicable"
            }

            this.validator.validateAndSubmit(null, false, formData => {
                return new Promise((resolve, reject) => {
                    this.saveAndContinue()
                        .then(() => {
                            this.submitting = false;
                            resolve();
                        })
                        .catch(error => {
                            this.submitting = false;
                            reject(error);
                        });
                });
            })
            .catch(error => {
                this.submitting = false;
            });
        },

        getQuestion(key){
            if (typeof key !== 'string') {
                return key;
            }
            return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).replace(/\s?Advised$/, '');
        },
        setModal(modalData){
            this.modalData = modalData;
            this.modalOpen = true;
        },
        closeModal() {
            this.modalOpen = false;
            this.modalData = null;
        }
    },

    computed: {
        ...mapState('ValuationTool', ['step', 'valuation', 'selectedVehicle']),
        ...mapState('ValuationExtended', ['mechanicalCondition', 'vehicleHealthCheck', 'displayExtraQuestions']),
        ...mapState("Auth", ['authenticated']),
        ...mapGetters('ValuationTool', ['vehicleImage']),

        formattedAmount() {
            return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP',  minimumFractionDigits: 0,
            maximumFractionDigits: 0 }).format(this.valuation.valuation);
        },

        NumberWornTyres: {
            get() {
                return this.$store.state.ValuationExtended.mechanicalCondition.NumberWornTyres;
            },
            set(value) {
                this.setNumberWornTyres(value);
            }
        },

        NumberOfWorkingKeys: {
            get() {
                return this.$store.state.ValuationExtended.mechanicalCondition.NumberOfWorkingKeys;
            },
            set(value) {
                this.setWorkingKeyCount(value);
            }
        },

        LockingWheelNutPresent: {
            get() {
                return this.$store.state.ValuationExtended.mechanicalCondition.LockingWheelNutPresent;
            },
            set(value) {
                this.setLockingWheelNutPresent(value);
            }
        },

        TimingKitReplaced: {
            get() {
                return this.$store.state.ValuationExtended.mechanicalCondition.TimingKitReplaced;
            },
            set(value) {
                this.setTimingKitReplaced(value);
            }
        },

        FunctioningMediaScreen: {
            get() {
                return this.$store.state.ValuationExtended.mechanicalCondition.FunctioningMediaScreen;
            },
            set(value) {
                this.setFunctioningMediaScreen(value);
            }
        },

        ChargingCablePresent: {
            get() {
                return this.$store.state.ValuationExtended.mechanicalCondition.ChargingCablePresent;
            },
            set(value) {
                this.setChargingCablePresent(value);
            }
        },

        formattedDisplayExtraQuestions() {
            const healthCheckData = this.vehicleHealthCheck;
            const filteredEntries = Object.entries(healthCheckData).filter(([key, obj]) => obj.display === true);
            return Object.fromEntries(filteredEntries.map(([key, obj]) => [key, obj]));
        },

        displayFrontBrakePadsQuestion(){
            return this.vehicleHealthCheck.FrontBrakePadsAdvised.display === false;
        },
        displayRearBrakePadsQuestion(){
            return this.vehicleHealthCheck.RearBrakePadsAdvised.display === false;
        }

    },
    mounted() {
        this.validator = new ContactFormValidator({
            element: this.$refs['form'],
            vueForm: true,
            redesign: true,
        });
        this.goToPosition('valuation-start')
    },
}
</script>


// WEBPACK FOOTER //
// resources/assets/js/valuation-tool/components/ExactValuationSteps/MechanicalCondition.vue